import { ConditionalLink } from "components/conditionalLink";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import Modal from "components/DesignSystem/Modal/Modal";
import { FileInput } from "components/FileInput/FileInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { Info } from "components/icons/Info";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { Field, FieldProps } from "formik";
import { useToast } from "hooks/useToast";
import { useWindowSize } from "hooks/useWindowSize";
import { useUploadCOAfileMutation } from "store/apis/chartOfAccounts";
import { BALANCE_SHEET_CSV_URL } from "types/contants/sampleExcelSheet";
import { BackendError } from "types/utils/error";
import { ImportCOAHeader } from "./ImportCOA";
import * as Yup from "yup";

type COAFormValues = {
  start_date: string;
  coa_csv: File;
  balance_sheet_csv: File | null;
  mark_opening_balances_zero: boolean;
};

type AddDetailsStepProps = {
  onNext: () => void;
  coaCSV: File;
  entityId: string;
  setImportProcessId: (id: string) => void;
};

export const AddDetailsStep = ({
  onNext,
  coaCSV,
  entityId,
  setImportProcessId,
}: AddDetailsStepProps) => {
  const { alertToast } = useToast();
  const [uploadCOAfile] = useUploadCOAfileMutation();
  const { isMobile } = useWindowSize();

  const handleSubmit = async (values: COAFormValues) => {
    try {
      if (!values.balance_sheet_csv) {
        return;
      }

      const { coa_import_state_id } = await uploadCOAfile({
        payload: {
          mark_opening_balances_zero: values.mark_opening_balances_zero,
          coa_csv: coaCSV,
          balance_sheet_csv: values.balance_sheet_csv,
          start_date: dayjs(values.start_date).format(YYYY_MM_DD),
        },
        entityId,
      }).unwrap();
      setImportProcessId(coa_import_state_id);
      onNext();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  return (
    <Modal.Content size="xl" asChild useCustomOverlay>
      <FormikForm<COAFormValues>
        initialValues={{
          start_date: "",
          coa_csv: coaCSV,
          balance_sheet_csv: null,
          mark_opening_balances_zero: false,
        }}
        validationSchema={Yup.object().shape({
          start_date: Yup.date().required("Start date is required"),
          coa_csv: Yup.mixed().required("COA CSV is required"),
          balance_sheet_csv: Yup.mixed().required(
            "Balance sheet CSV is required"
          ),
          mark_opening_balances_zero: Yup.boolean(),
        })}
        onSubmit={handleSubmit}
      >
        {({
          values: { balance_sheet_csv },
          submitForm,
          isSubmitting,
          isValid,
        }) => {
          return (
            <>
              <ImportCOAHeader />
              <Modal.Body className="t-flex t-flex-col t-gap-5 sm:t-px-4">
                <DateField
                  name="start_date"
                  dateProps={{
                    label:
                      "Date till which the Chart of Accounts is being imported",
                    required: true,
                    placeholder: DISPLAY_DATE,
                  }}
                />
                <div className="t-flex t-flex-col t-gap-2">
                  <FileInput
                    withForm
                    name="balance_sheet_csv"
                    label={
                      <>
                        Balance sheet (Format supported: csv)
                        <ConditionalToolTip
                          condition={
                            <div className="t-text-center t-w-60">
                              Upload the Balance Sheet as of the date till which
                              Chart of Accounts in being imported
                            </div>
                          }
                          side={isMobile ? "top" : "right"}
                          collisionPadding={isMobile ? 16 : undefined}
                        >
                          <span className="t-ml-1">
                            <Info />
                          </span>
                        </ConditionalToolTip>
                      </>
                    }
                    accept={{
                      "text/csv": [".csv"],
                    }}
                    file={
                      balance_sheet_csv
                        ? {
                            name: balance_sheet_csv?.name,
                            file_type: "CSV",
                            uuid: "",
                            is_previewable: false,
                          }
                        : null
                    }
                    required
                  />
                  <div className="t-text-text-30 t-text-body-sm t-italic">
                    If importing from Quickbooks, you can find the Balance Sheet
                    here: Reports {`->`} Balance Sheet {`->`} Export as CSV.{" "}
                    <ConditionalLink
                      to={BALANCE_SHEET_CSV_URL}
                      className="t-text-purple t-mr-1 hover:!t-underline"
                    >
                      Click here
                    </ConditionalLink>
                    to download a sample report.
                  </div>
                </div>

                <Field name="mark_opening_balances_zero">
                  {({ field }: FieldProps) => (
                    <Checkbox {...field} label="Set opening balances to 0" />
                  )}
                </Field>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="reset" disabled={isSubmitting}>
                    Cancel
                  </Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  onClick={submitForm}
                  disabled={isSubmitting || !isValid}
                  isLoading={isSubmitting}
                >
                  Next
                </Button>
              </Modal.FooterButtonGroup>
            </>
          );
        }}
      </FormikForm>
    </Modal.Content>
  );
};
